import { createRouter, createWebHistory } from 'vue-router'
import RegisterView from '../views/RegisterView.vue'
import store from '../store'
import Swal from 'sweetalert2'

const routes = [
  {
    path: '/',
    name: 'home',
    redirect: '/dashboard',
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
    meta: { requiresGuest: true },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { requiresGuest: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: true, // ✅ Se bloquea cuando expira
    },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('../views/UsersView.vue'),
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: true, // ✅ Se bloquea cuando expira
    },
  },
  {
    path: '/clientes',
    name: 'clientes',
    component: () => import('../views/ClientsView.vue'),
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: true, // ✅ Se bloquea cuando expira
    },
  },
  {
    path: '/agenda',
    name: 'agenda',
    component: () => import('../views/AgendaView.vue'),
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: true, // ✅ Se bloquea cuando expira
    },
  },
  {
    path: '/rutas-optimizadas',
    name: 'RutasOptimizadas',
    component: () => import('@/views/RutaOptimizadaView.vue'),
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: true, // ✅ Se bloquea cuando expira
    },
  },
  {
    path: '/company-settings',
    name: 'company-settings',
    alias: '/configuracion',
    component: () => import('../views/CompanySettingsView.vue'),
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: false, // ✅ NO se bloquea (siempre accesible)
    },
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => import('../views/ReportsView.vue'),
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: true, // ✅ Se bloquea cuando expira
    },
  },
  {
    path: '/visits',
    name: 'visits',
    component: () => import('../views/VisitasView.vue'), 
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: true, // ✅ Se bloquea cuando expira
    },
  },
  {
    path: '/tipos-eventos',
    name: 'TiposEventos',
    component: () => import('../views/catalogos/EventosTiposView.vue'), 
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: true, // ✅ Se bloquea cuando expira
    },
  },
  {
    path: '/zonas',
    name: 'zonas',
    component: () => import('../views/catalogos/ZonasView.vue'),
    meta: { 
      requiresAuth: true,
      bloqueableEnExpiracion: true,
    },
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

// ==========================================
// GUARDS DE NAVEGACIÓN
// ==========================================

router.beforeEach((to, from, next) => {
  // Verificar token en localStorage Y en el store
  const token = localStorage.getItem('token')
  const isAuthenticated = token && store.getters.isAuthenticated
  
  // ✅ NUEVO: Verificar si la empresa está expirada
  const empresaExpirada = localStorage.getItem('empresa_expirada') === 'true'

   // ==========================================
  // 1. VALIDACIÓN DE AUTENTICACIÓN
  // ==========================================
  if (to.meta.requiresAuth) {
    if (!isAuthenticated) {
     // console.log('❌ No autenticado, redirigiendo a /login')
      next('/login')
      return
    }
  } else if (to.meta.requiresGuest) {
    if (isAuthenticated) {
     // console.log('✅ Ya autenticado, redirigiendo a /dashboard')
      next('/dashboard')
      return
    } else {
     // console.log('✅ No autenticado, acceso permitido a ruta guest')
      next()
      return
    }
  }

  // ==========================================
  // 2. VALIDACIÓN DE EMPRESA EXPIRADA
  // ==========================================
  if (empresaExpirada && isAuthenticated) {
    // Rutas PERMITIDAS cuando está expirado
    const rutasPermitidas = [
      '/company-settings',
      '/configuracion', // alias
      '/login',
      '/register'
    ]

    // Si intenta ir a una ruta bloqueada
    if (!rutasPermitidas.includes(to.path) && to.meta.bloqueableEnExpiracion !== false) {
     // console.log('🔒 Navegación bloqueada - Empresa expirada')
      
      // Mostrar alerta
      Swal.fire({
        icon: 'warning',
        title: 'Acceso Restringido',
        html: `
          <p class="mb-2">Tu suscripción ha expirado.</p>
          <p class="text-body-2 text-grey">
            Debes renovar tu plan para acceder a esta sección.
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: '🚀 Renovar Ahora',
        cancelButtonText: 'Volver',
        confirmButtonColor: '#FF9800',
        cancelButtonColor: '#6c757d',
        allowOutsideClick: false
      }).then((result) => {
        if (result.isConfirmed) {
          // Redirigir a configuración con parámetros
          next({
            path: '/company-settings',
            query: { 
              upgrade_required: 'true',
              expired: 'true'
            }
          })
        } else {
          // Quedarse en la ruta actual o volver
          if (from.path && from.path !== to.path) {
            next(false) // Cancelar navegación
          } else {
            next('/company-settings') // Si no hay "from", ir a configuración
          }
        }
      })
      
      return // Importante: detener ejecución
    }

   // console.log('✅ Navegación permitida a ruta no bloqueada en modo expirado')
  }

  // ==========================================
  // 3. CONTINUAR NAVEGACIÓN NORMAL
  // ==========================================
 // console.log('✅ Navegación permitida')
  next()
})

// ==========================================
// GUARD DESPUÉS DE NAVEGACIÓN (opcional)
// ==========================================
router.afterEach((to, from) => {
  // Log para debugging
 

  // Scroll to top en cada navegación
  window.scrollTo(0, 0)
})

export default router